/**
 * Canonical cycle cache keys (#2240).
 *
 * Cycle CRUD is (still) split between two hook families —
 * `packages/cycles/hooks/useCycles` (canonical) and the legacy set in
 * `packages/patients/hooks/usePatient`. Until consumers finish migrating,
 * both families and every writer MUST derive their query keys from this
 * factory: the concrete staleness bug behind this issue was a legacy
 * mutation invalidating only `["cycles"]` while the canonical
 * `useActiveCycle` caches under `["active-cycle"]`, so Home kept showing a
 * stale active cycle after an update.
 */

export const cycleKeys = {
  /** Every cycle-list query, any tenant/patient. */
  all: ["cycles"] as const,
  list: (tenantId: string, patientId: string) =>
    ["cycles", tenantId, patientId] as const,
  /** The server-resolved active cycle (canonical useActiveCycle). */
  activeAll: ["active-cycle"] as const,
  active: (tenantId: string, patientId: string) =>
    ["active-cycle", tenantId, patientId] as const,
  /** Phases — shared by usePhases (patients) and useCyclePhases (cycles). */
  phasesAll: ["cycle-phases"] as const,
  phases: (tenantId: string, cycleId: string) =>
    ["cycle-phases", tenantId, cycleId] as const,
  /**
   * The patient's own pregnancy status for a cycle (`useCyclePregnancyStatus`).
   *
   * Here rather than in the pregnancy package because a *cycle outcome* write
   * changes what that endpoint returns: the assertion's `resolutionStatus` is
   * derived from the cycle's current positive outcome, so correcting a beta
   * away from positive makes a stored status unresolved and the endpoint stops
   * serving it. A writer that invalidated only the cycle keys would leave the
   * card reading "Pregnant" from a five-minute-stale cache.
   */
  pregnancyStatusAll: ["cycle-pregnancy-status"] as const,
  pregnancyStatus: (cycleId: string) =>
    ["cycle-pregnancy-status", cycleId] as const,
} as const;

/**
 * Every prefix a cycle mutation must invalidate. Any write to a cycle, its
 * outcome, or its phases can change the list, the resolved active cycle,
 * and phase-derived state — invalidating a subset is how the two hook
 * families drifted out of sync.
 */
export function cycleMutationInvalidationKeys(): readonly (readonly string[])[] {
  return [cycleKeys.all, cycleKeys.activeAll, cycleKeys.phasesAll];
}
